import { isFunction } from 'lodash';
import { assign } from 'xstate';
import { lmiReviewMachine } from '~/machines/lmi-review';
import { isAddressComplete, solarMachine } from '~/machines/solar-funnel';
import { LDClient } from '~/providers/launch-darkly';
import { LDFlags } from '~/providers/launch-darkly/flags';
import { agreeToTerms } from '~/services/agree-to-terms';
import { fetchLmiForm } from '~/services/fetch-lmi-form';
import { signUpUser } from '~/services/sign-up-user';
import { ContractTag, ContractType, ContractVersion } from '~/typings/graphql';
import { fetchContractLanguage } from '~/utils/fetch-contract-language';
import { getInitialState } from '~/utils/funnels';
import { minnesotaLmiRedirects } from '~/utils/redirects';
import { config } from './config';

export const funnelMachine = () =>
  solarMachine({
    id: config.id,
    initialState: getInitialState(config.id, solarMachine),
    utility: {
      context: { redirect: minnesotaLmiRedirects },
    },
    user: {
      context: { requirePhone: true, showInviteCode: true, isMinnesotaLMI: true },
    },
    legal: {
      context: {
        contractTags: [ContractTag.MnLmi],
      },
      services: {
        signTerms: async context => {
          /**
           * Remove self attestation contract from the list of contracts
           * because user will sign it from docusign.
           */
          const contracts = context.vnmSolarProjectContracts.filter(
            c => c.contractType !== ContractType.Lmi,
          );
          await agreeToTerms({
            userId: context.funnelPacket.user.id,
            utilityAccountId: context.funnelPacket.utilityAccount.id,
            vnmSolarProjectContracts: contracts,
          });
          return undefined;
        },
      },
    },
  }).withConfig({
    actions: {
      setDocusignContracts: assign((_context, event) => {
        const { docuSignContracts, lmiStatus } = event.data;
        if (lmiStatus === 'isLmi') {
          return {
            docuSignContracts,
            lmiStatus,
          };
        }
        return {
          docuSignContracts: docuSignContracts?.filter(contract => contract.contractType !== 'lmi'),
          lmiStatus,
        };
      }),
    },
    guards: {
      isMinnesotaLMI: (_context, _event) => {
        return true;
      },
      isMnLmiWithAddress: (context, _event) => {
        return isAddressComplete(context);
      },
      isMnLmiWithoutAddress: (context, _event) => {
        return !isAddressComplete(context);
      },
      isMnLmiWithSelfAttestation: (context, _event) => {
        const { funnelPacket, userDocusignEnvelopes = [] } = context;
        const { enrollmentExperience } = funnelPacket?.user?.originationSource || {};
        const { lmiSelfAttestation } = funnelPacket?.user?.metaData || {};
        /**
         * this is a bit tricky. What we want to find is, when resuming,
         * do we need to send the user to the self attestation page
         * or legal page?
         *
         * the simplest case is,
         * if lmistatus is 'isLmi', then the user has, at some point,
         * filled the self attestation form. but there is a chance that
         * the user closed before signing the docusign. so we return
         * based on whether userDocusignEnvelopes.length > 0
         *
         * the other case is,
         * if lmistatus is 'notLmi', then the user has skipped the self attestation form.
         * but now we can't just decide based on the size of the envelope array because
         * we don't know if the user skipped from partner_provided(has 1 usage request form)
         * or from utility_connect(has no docusign).
         *
         * so to confirm this we also check for enrollmentExperience.
         *
         * if enrollmentExperience is partner_provided, then we know that the user
         * skipped from partner_provided and we should send them to the self attestation page
         * if there are no envelopes and to legal page if there are envelopes.
         *
         * if enrollmentExperience is not partner_provided, then we know that the user
         * skipped from utility_connect and we should send them to the legal page as
         * UC/Skip combo has no docusign.
         *
         * if the lmistatus is null/undefined, the final else part gets executed
         * this means that the user has not filled the self attestation form yet.
         * so we send them to the self attestation page.
         */
        if (lmiSelfAttestation?.lmiStatus === 'isLmi') {
          return userDocusignEnvelopes.length > 0;
        } else if (lmiSelfAttestation?.lmiStatus === 'notLmi') {
          return enrollmentExperience === 'partner_provided'
            ? userDocusignEnvelopes.length > 0
            : true;
        } else {
          return false;
        }
      },
    },
    services: {
      signUpUser: async context => {
        const { plan } = await signUpUser({
          utilityAccountId: context.funnelPacket!.utilityAccount.id,
          planId: context.funnelPacket!.customPlan.id,
          enrollmentDataSourceName:
            context.enrollmentSelection! ||
            context.funnelPacket!.utilityAccount.enrollmentDataSourceName ||
            'none',
          billingExperienceType: context.billingSelection! || 'ucb',
          track: context.funnelTrack || context.funnelPacket!.utility.funnelTrack,
          funnelVnmProjectId:
            context.funnelVnmProjectId ||
            context.queryParams.projectId! ||
            context.funnelPacket?.funnelVnmProjectId! ||
            LDClient.variation(LDFlags.ffDefaultSignupProjectId)?.project_id,
        });
        return { planId: plan.id };
      },
      lmiReviewMachine: lmiReviewMachine().withConfig({
        services: {
          fetchTerms: async context => {
            const contractTags = isFunction(context.contractTags)
              ? context.contractTags(context)
              : context.contractTags;

            const { docuSignContracts, showLmi } = await fetchLmiForm({
              stateId: context.funnelPacket.utility.state.id,
              contractTags,
            });

            const data = await fetchContractLanguage(docuSignContracts, [], showLmi);

            return {
              docuSignContracts: data.docuSignContracts as ContractVersion[],
              vnmSolarProjectContracts: data.vnmSolarProjectContracts as ContractVersion[],
              showLmi: data.showLmi!,
            };
          },
        },
      }),
    },
  });
